export interface EncodedImage {
  base64: string;
  mimeType: string;
  /** Bytes of the encoded JPEG, for showing how much upload was saved. */
  byteLength: number;
  width: number;
  height: number;
}

/**
 * Downscale and re-encode a photo before upload.
 *
 * A modern phone camera produces 3–8MB images at 4000px wide. Receipts are
 * legible far below that, and the extra pixels cost upload time and image
 * tokens without improving the read. 1500px on the long edge keeps small print
 * readable while cutting a typical photo to a few hundred KB.
 */
export async function encodeForUpload(file: File, maxEdge = 1500): Promise<EncodedImage> {
  // Phones store landscape shots sideways with an EXIF flag. Apply it here so
  // the model, the thumbnail and the viewer all see the receipt upright.
  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
  } catch {
    bitmap = await createImageBitmap(file);
  }
  const scale = Math.min(1, maxEdge / Math.max(bitmap.width, bitmap.height));
  const width = Math.round(bitmap.width * scale);
  const height = Math.round(bitmap.height * scale);

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;

  const context = canvas.getContext('2d');
  if (!context) throw new Error('could not get a 2d canvas context');
  context.drawImage(bitmap, 0, 0, width, height);
  bitmap.close();

  const blob = await new Promise<Blob | null>((resolve) =>
    // 0.85 is where JPEG artefacts stay clear of thermal-print text.
    canvas.toBlob(resolve, 'image/jpeg', 0.85),
  );
  if (!blob) throw new Error('could not encode the image');

  const buffer = new Uint8Array(await blob.arrayBuffer());
  let binary = '';
  for (let i = 0; i < buffer.length; i += 0x8000) {
    binary += String.fromCharCode(...buffer.subarray(i, i + 0x8000));
  }

  return { base64: btoa(binary), mimeType: 'image/jpeg', byteLength: blob.size, width, height };
}
